import React, { useEffect, useState } from 'react';
import { Table, Input, Space, Tag, Select, Empty, Checkbox } from 'antd';
import type { CheckboxChangeEvent } from 'antd/es/checkbox';
import axios from 'axios';
import { TaskData } from '../../../../types/TaskData';
import './SystemPlanningOrderTable.css';
import { StatusUtils } from "../../../../utils/StatusUtils";
import {getConfig} from "../../../../config";
import {useProjectContext} from "../../../../context/ProjectContext";

const { Option } = Select;
const { API_BASE_URL } = getConfig();

interface SystemPlanningOrderTableProps {
    onSelectRow: (row: TaskData | null) => void; // Pass null if deselected
    refreshKey: number;
}

const SystemPlanningOrderTable: React.FC<SystemPlanningOrderTableProps> = ({ onSelectRow, refreshKey }) => {
    const [data, setData] = useState<TaskData[]>([]);
    const [originalData, setOriginalData] = useState<TaskData[]>([]); // To store unfiltered data
    const { selectedProject, selectedManagerNo } = useProjectContext();
    const [selectedRow, setSelectedRow] = useState<TaskData | null>(null);
    const [globalSearch, setGlobalSearch] = useState('');
    const [showAllData, setShowAllData] = useState(false);
    const [arraysEqual, setArraysEqual] = useState(false);

    useEffect(() => {
        if (selectedProject)    {
            fetchData(selectedProject);
        }
    }, [selectedProject, refreshKey]);

    const fetchData = async (projectNo: string) => {
        try {
            const response = await axios.get<TaskData[]>(
                `${API_BASE_URL}/getReadyToDoTasks?auth=VdjCKidks7qTEyuU6QeJtezZ3PeCHhZQ2yHJ3kPtcfTN8BjVqU&projectNo=${projectNo}&type=systemPlanning&managerNo=${selectedManagerNo}`
            );
            setOriginalData(response.data);
            setData(response.data.filter(item => item.activityStatus === 'READY'));
            setArraysEqual(
                JSON.stringify(originalData) === JSON.stringify(data)
            );
        } catch (error) {
            console.error('Error fetching data:', error);
        }
        setSelectedRow(null); // Reset selected row on data fetch
    };

    const handleCheckboxChange = (e: CheckboxChangeEvent) => {
        setShowAllData(e.target.checked);
        setData(e.target.checked ? originalData : originalData.filter(item => item.activityStatus === 'READY'));
    };


    // Filter data based on global search input
    const filteredData = data.filter(item =>
        item.orderCardId.toLowerCase().includes(globalSearch.toLowerCase()) ||
        (item.jobCodeDesc?.toLowerCase() ?? '').includes(globalSearch.toLowerCase()) ||
        item.faz.toString().includes(globalSearch)
    );

    const columns = [
        {
            title: 'Produkt',
            dataIndex: 'jobCodeDesc',
            key: 'jobCodeDesc',
            width: 350,
            sorter: (a: TaskData, b: TaskData) => (a.jobCodeDesc ?? '').localeCompare(b.jobCodeDesc ?? ''),
            render: (text: string, record: TaskData) => (
                <>
                    {text}
                    {record.korrex && ` (Korrex)`}
                </>
            ),
        },
        {
            title: 'Start',
            dataIndex: 'faz',
            key: 'faz',
            sorter: (a: TaskData, b: TaskData) => a.faz.localeCompare(b.faz),
            render: (faz: string) => {
                const [year, month, day] = faz.split('-');
                return `${day}.${month}.${year}`;
            },
        },
        ...(selectedProject === "Alle"
            ? [
                {
                    title: 'Projekt',
                    dataIndex: 'projectNo',
                    key: 'projectNo',
                    sorter: (a: TaskData, b: TaskData) => (a.projectNo ?? '').localeCompare(b.projectNo ?? ''),
                },
            ]
            : []),
        {
            title: 'Status',
            dataIndex: 'activityStatus',
            key: 'activityStatus',
            sorter: (a: TaskData, b: TaskData) => (a.activityStatus ?? '').localeCompare(b.activityStatus ?? ''),
            render: (status: 'DONE' | 'READY' | 'NOT_READY' | 'PENDING') => {
                const color = StatusUtils.getColor(status);
                const translatedStatus = StatusUtils.getTranslatedStatus(status);
                return <Tag color={color}>{translatedStatus}</Tag>;
            },
        }
    ];

    const rowSelection = {
        type: 'radio' as const, // Single selection
        selectedRowKeys: selectedRow ? [selectedRow.id] : [],
        onChange: (selectedRowKeys: React.Key[], selectedRows: TaskData[]) => {
            setSelectedRow(selectedRows[0] || null);
            onSelectRow(selectedRows[0] || null);
        },
        getCheckboxProps: (record: TaskData) => ({
            disabled: record.activityStatus !== 'READY', // Disable radio button if status is not READY
        }),
    };

    const handleRowClick = (record: TaskData) => {
        if (record.activityStatus === 'READY') {
            const isSelected = selectedRow?.id === record.id;
            const newSelectedRow = isSelected ? null : record;
            setSelectedRow(newSelectedRow);
            onSelectRow(newSelectedRow);
        }
    };

    return (
        <div style={{ padding: '20px' }}>
            <Space style={{ marginBottom: 16, display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <Checkbox
                        onChange={handleCheckboxChange}
                        checked={showAllData}
                        disabled={!arraysEqual}
                        style={{ marginRight: '8px' }}
                    >
                        Alle Daten für 'Systemplanungsbeauftragung' anzeigen
                    </Checkbox>
                </div>
            </Space>


            <Input
                placeholder="Suche in Produkt oder Start"
                value={globalSearch}
                onChange={(e) => setGlobalSearch(e.target.value)}
                style={{ marginBottom: 16, width: '100%' }}
            />

            <Table
                rowSelection={{
                    ...rowSelection,
                }}
                columns={columns}
                dataSource={filteredData}
                rowKey="id"
                pagination={{ pageSize: 100 }}
                scroll={{ y: 700 }}
                locale={{
                    emptyText: <Empty description="Keine Produkte zur Beauftragung vorhanden" />
                }}
                onRow={(record) => ({
                    onClick: () => handleRowClick(record),
                })}
                rowClassName={(record) =>
                    selectedRow?.id === record.id ? 'selected-row' : ''
                }
            />
        </div>
    );
};

export default SystemPlanningOrderTable;
